import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import UserModal from './UserModal/UserModal';
import WalletModal from './WalletModal';
import DepositModal from './DepositModal';
import DepositedModal from './DepositedModal';
import PnlModal from './PnlModal';
import { getUserOverview, saveUser, markFundPaid } from '../../services/UsersService';
import './UserDetail.css';

function formatUsd(value, signed = false) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '0,00';
    const formatted = number.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    if (!signed) return formatted;
    if (number > 0) return `+${formatted}`;
    return formatted;
}

function formatQty(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '0';
    return number.toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 8
    });
}

function formatWhen(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

function pairLabel(symbol) {
    return String(symbol || '').replace(/USDC$/, '/USDC').replace(/USDT$/, '/USDC');
}

function toneClass(value) {
    const number = Number(value);
    if (number > 0) return 'text-success';
    if (number < 0) return 'text-danger';
    return 'text-muted';
}

function apiError(err) {
    const raw = err?.response ? err.response.data : err?.message;
    if (typeof raw === 'string') return raw;
    if (raw && raw.message) return raw.message;
    return raw ? JSON.stringify(raw) : 'Não foi possível carregar o usuário.';
}

function UserDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState({ type: '', text: '' });
    const [walletStopDraft, setWalletStopDraft] = useState('');
    const [savingStop, setSavingStop] = useState(false);
    const [payingFund, setPayingFund] = useState(false);

    function loadOverview() {
        setLoading(true);
        getUserOverview(id)
            .then(result => {
                setData(result);
                const stop = result?.user?.walletStopUsdc;
                setWalletStopDraft(stop != null && stop !== '' ? String(stop) : '');
                setLoading(false);
            })
            .catch(err => {
                setNotification({ type: 'error', text: apiError(err) });
                setLoading(false);
            });
    }

    async function saveWalletStop() {
        if (!user.id || savingStop) return;
        setSavingStop(true);
        try {
            const parsed = walletStopDraft === '' ? null : parseFloat(walletStopDraft);
            if (parsed != null && !(parsed > 0))
                throw new Error('Informe um valor maior que zero, ou deixe vazio para desligar.');
            await saveUser(user.id, { walletStopUsdc: parsed });
            setNotification({ type: 'success', text: parsed ? `Stop de carteira em ${parsed.toFixed(2)} USDC.` : 'Stop de carteira desligado.' });
            loadOverview();
        } catch (err) {
            setNotification({ type: 'error', text: apiError(err) });
        } finally {
            setSavingStop(false);
        }
    }

    async function onMarkFundPaid() {
        if (payingFund || !user.id) return;
        const payable = (Number(data.usdcFund?.usdtAvailable) || 0) + (Number(data.usdcFund?.usdcPending) || 0);
        const pending = Boolean(data.fundPayout);
        if (!(payable > 0) && !pending) return;
        const ok = window.confirm(
            pending
                ? `Marcar o saque pedido de ${user.name || user.email} como pago? O fundo será zerado.`
                : `Marcar o fundo de ${user.name || user.email} (${formatUsd(payable)} USDT) como pago?`
        );
        if (!ok) return;
        setPayingFund(true);
        try {
            const result = await markFundPaid(user.id);
            setNotification({ type: 'success', text: result?.message || 'Fundo marcado como pago.' });
            loadOverview();
        } catch (err) {
            setNotification({ type: 'error', text: apiError(err) });
        } finally {
            setPayingFund(false);
        }
    }

    useEffect(() => {
        loadOverview();
    }, [id]);

    const user = data?.user || {};
    const vault = data?.vault || {};
    const wallet = data?.wallet || [];
    const platformWallet = data?.platformWallet || [];
    const allocated = Boolean(data?.usesAllocatedWallet);
    const entries = vault.entries || [];
    const net = Number(vault.net || 0);
    const modalUser = data
        ? {
            ...user,
            custodyUsdc: data.tradableUsdc ?? data.custodyUsdc,
            tradableUsdc: data.tradableUsdc,
            vaultLocked: data.vaultLocked,
            walletTotal: data.walletTotal,
            walletStopUsdc: user.walletStopUsdc,
            walletStopHitAt: user.walletStopHitAt,
            custodyVault: {
                gained: vault.gained,
                lost: vault.lost,
                net: vault.net,
                profitCount: vault.count,
                lossCount: vault.lossCount
            }
        }
        : {};

    return (
        <>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <Link to="/users" className="text-muted small">← Voltar para usuários</Link>
                        <h2 className="h4 mt-2 mb-1">
                            {user.name || (loading ? 'Carregando...' : 'Usuário')}
                            {user.isActive
                                ? <span className="badge bg-success ms-2">Ativo</span>
                                : <span className="badge bg-danger ms-2">Inativo</span>}
                            {user.isCustody
                                ? <span className="badge bg-info ms-2">Custódia</span>
                                : <></>}
                            {allocated
                                ? <span className="badge bg-primary ms-2">Carteira alocada</span>
                                : <></>}
                        </h2>
                        <p className="text-muted mb-0">{user.email || ''}</p>
                    </div>
                    <div className="btn-toolbar mb-2 mb-md-0">
                        <button type="button" className="btn btn-outline-gray-600 me-2" onClick={loadOverview} disabled={loading}>
                            Atualizar
                        </button>
                        {user.isCustody
                            ? (
                                <>
                                    <button type="button" className="btn btn-success me-2" data-bs-toggle="modal" data-bs-target="#modalDeposit">
                                        Depositar
                                    </button>
                                    <button type="button" className="btn btn-outline-primary me-2" data-bs-toggle="modal" data-bs-target="#modalWallet">
                                        Editar saldo
                                    </button>
                                </>
                            )
                            : <></>}
                        {user.isCustody
                            ? (
                                <button type="button" className="btn btn-outline-secondary me-2" data-bs-toggle="modal" data-bs-target="#modalDeposited">
                                    Editar depositado
                                </button>
                            )
                            : <></>}
                        {user.isCustody
                            ? (
                                <button type="button" className="btn btn-outline-warning me-2" data-bs-toggle="modal" data-bs-target="#modalPnl">
                                    Corrigir PnL
                                </button>
                            )
                            : <></>}
                        {user.id
                            ? (
                                <button type="button" className="btn btn-primary" data-bs-toggle="modal" data-bs-target="#modalUser">
                                    Editar cadastro
                                </button>
                            )
                            : <></>}
                    </div>
                </div>

                {loading && !data && (
                    <div className="card border-0 shadow mb-4">
                        <div className="card-body py-5 text-center text-muted">Carregando informações do usuário...</div>
                    </div>
                )}

                {data && (
                    <>
                        <div className="row">
                            <div className="col-12 col-md-4 mb-4">
                                <div className="card border-0 shadow user-detail-metric">
                                    <div className="card-body">
                                        <span>Saldo livre</span>
                                        <strong>{formatUsd(data.tradableUsdc)} USDC</strong>
                                        <small>
                                            {allocated
                                                ? `Teto do Predador ${formatUsd(user.predadorMaxUsdc)} · PnL hoje ${formatUsd(data.pnl, true)}`
                                                : 'Disponível para novas compras'}
                                            {Number(data.custodyReserved) > 0
                                                ? ` · ${formatUsd(data.custodyReserved)} USDC reservado para clientes de custódia`
                                                : ''}
                                            {!allocated && Number(data.pnl) !== 0 && Number.isFinite(Number(data.pnl))
                                                ? ` · PnL hoje ${formatUsd(data.pnl, true)} USDC`
                                                : ''}
                                            {allocated && Number(data.deployed) > 0
                                                ? ` · ${formatUsd(data.deployed)} USDC em posições abertas`
                                                : ''}
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-4 mb-4">
                                <div className="card border-0 shadow user-detail-metric">
                                    <div className="card-body">
                                        <span>{allocated ? 'Carteira do usuário' : 'Carteira'}</span>
                                        <strong>{formatUsd(data.walletTotal)} USDC</strong>
                                        <small>
                                            {allocated
                                                ? `${wallet.length} moeda${wallet.length === 1 ? '' : 's'} alocadas (USDC livre + posições)`
                                                : `${wallet.length} moeda${wallet.length === 1 ? '' : 's'} com saldo`}
                                            {Number(user.walletStopUsdc) > 0
                                                ? ` · stop em ${formatUsd(user.walletStopUsdc)} USDC`
                                                : ''}
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-4 mb-4">
                                <div className="card border-0 shadow user-detail-metric">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start gap-2">
                                            <span>{user.isCustody ? 'PnL' : 'PnL hoje'}</span>
                                            {user.isCustody
                                                ? (
                                                    <button type="button" className="btn btn-sm btn-outline-warning" data-bs-toggle="modal" data-bs-target="#modalPnl">
                                                        Corrigir
                                                    </button>
                                                )
                                                : null}
                                        </div>
                                        <strong className={toneClass(Number(data.pnl != null ? data.pnl : net))}>{formatUsd(data.pnl != null ? data.pnl : net, true)} USDC</strong>
                                        <small>
                                            {user.isCustody && Number(data.depositedUsdc != null ? data.depositedUsdc : data.allocation) > 0
                                                ? `Versus ${formatUsd(data.depositedUsdc != null ? data.depositedUsdc : data.allocation)} USDC depositado em carteira`
                                                : !user.isCustody
                                                    ? `Versus ${formatUsd(data.dayOpenUsdc)} USDC no começo do dia (carteira Binance)`
                                                    : `Realizado ${formatUsd(vault.gained)} · Perdas ${formatUsd(vault.lost)}`}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {user.isCustody
                            ? (
                                <div className="card border-0 shadow mb-4">
                                    <div className="card-body">
                                        <span className="text-muted d-block">Fundo USDT (taxas a receber)</span>
                                        <strong className="h4 d-block mb-1">
                                            {formatUsd((Number(data.usdcFund?.usdtAvailable) || 0) + (Number(data.usdcFund?.usdcPending) || 0))} USDT
                                        </strong>
                                        <small className="text-muted d-block mb-3">
                                            10% de cada operação vencedora. Fica em USDT, separado do USDC da plataforma, e não vira BTC.
                                            {Number(data.usdcFund?.usdtTotal) > 0
                                                ? ` · acumulado ${formatUsd(data.usdcFund.usdtTotal)} USDT`
                                                : ''}
                                            {Number(data.usdcFund?.usdcPending) > 0
                                                ? ` · ${formatUsd(data.usdcFund.usdcPending)} USDC aguardando conversão`
                                                : ''}
                                        </small>
                                        {data.fundPayout
                                            ? (
                                                <div className="alert alert-warning py-2 mb-3">
                                                    Saque pedido em {formatWhen(data.fundPayout.requestedAt)} · {formatUsd(data.fundPayout.amount)} USDT
                                                    {data.fundPayout.pixKey ? ` · PIX ${data.fundPayout.pixKey}` : ''}
                                                </div>
                                            )
                                            : null}
                                        <button
                                            type="button"
                                            className="btn btn-success"
                                            disabled={payingFund || (!(Number(data.usdcFund?.usdtAvailable) > 0) && !(Number(data.usdcFund?.usdcPending) > 0) && !data.fundPayout)}
                                            onClick={onMarkFundPaid}
                                        >
                                            {payingFund ? 'Marcando...' : 'Marcar fundo como pago'}
                                        </button>
                                    </div>
                                </div>
                            )
                            : null}

                        <div className="row">
                            <div className="col-12 col-xl-4 mb-4">
                                <div className="card border-0 shadow h-100">
                                    <div className="card-body">
                                        <h3 className="h5 mb-3">Cadastro</h3>
                                        <dl className="user-detail-dl mb-0">
                                            <div>
                                                <dt>Telefone</dt>
                                                <dd>{user.phone || '—'}</dd>
                                            </div>
                                            <div>
                                                <dt>Chave PIX</dt>
                                                <dd className="text-break">{user.pixKey || '—'}</dd>
                                            </div>
                                            <div>
                                                <dt>Telegram</dt>
                                                <dd>{user.telegramChat || '—'}</dd>
                                            </div>
                                            <div>
                                                <dt>Plano</dt>
                                                <dd>{user.limit?.name || '—'}</dd>
                                            </div>
                                            <div>
                                                <dt>Cap Predador</dt>
                                                <dd>{user.predadorMaxUsdc ? `${formatUsd(user.predadorMaxUsdc)} USDC` : '—'}</dd>
                                            </div>
                                            <div>
                                                <dt>Stop de carteira</dt>
                                                <dd>
                                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            className="form-control form-control-sm"
                                                            style={{ maxWidth: 140 }}
                                                            placeholder="Desligado"
                                                            value={walletStopDraft}
                                                            onChange={event => setWalletStopDraft(event.target.value)}
                                                        />
                                                        <span>USDC</span>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={saveWalletStop}
                                                            disabled={savingStop || !user.id}
                                                        >
                                                            {savingStop ? 'Salvando...' : 'Salvar'}
                                                        </button>
                                                    </div>
                                                    <small className="text-muted d-block mt-1">
                                                        Se a carteira cair a este valor, vende tudo, encerra os monitores e dolariza.
                                                    </small>
                                                    {user.walletStopHitAt
                                                        ? (
                                                            <small className="text-warning d-block">
                                                                Disparou em {formatWhen(user.walletStopHitAt)}. Altere o valor para rearmar.
                                                            </small>
                                                        )
                                                        : null}
                                                </dd>
                                            </div>
                                            {user.isCustody
                                                ? (
                                                    <div>
                                                        <dt>Capital depositado</dt>
                                                        <dd>
                                                            <span className="me-2">
                                                                {Number(data.depositedUsdc != null ? data.depositedUsdc : data.allocation) > 0
                                                                    ? `${formatUsd(data.depositedUsdc != null ? data.depositedUsdc : data.allocation)} USDC`
                                                                    : '—'}
                                                            </span>
                                                            <button type="button" className="btn btn-sm btn-outline-secondary" data-bs-toggle="modal" data-bs-target="#modalDeposited">
                                                                Editar
                                                            </button>
                                                        </dd>
                                                    </div>
                                                )
                                                : null}
                                            <div>
                                                <dt>Criado em</dt>
                                                <dd>{formatWhen(user.createdAt)}</dd>
                                            </div>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-xl-8 mb-4">
                                <div className="card border-0 shadow h-100">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h3 className="h5 mb-0">Moedas na carteira</h3>
                                            {user.isCustody
                                                ? (
                                                    <div className="d-flex gap-2">
                                                        <button type="button" className="btn btn-sm btn-success" data-bs-toggle="modal" data-bs-target="#modalDeposit">
                                                            Depositar
                                                        </button>
                                                        <button type="button" className="btn btn-sm btn-outline-primary" data-bs-toggle="modal" data-bs-target="#modalWallet">
                                                            Editar saldo
                                                        </button>
                                                    </div>
                                                )
                                                : <></>}
                                        </div>
                                        {data.walletError
                                            ? <p className="text-muted mb-0">{data.walletError}</p>
                                            : <></>}
                                        {!data.walletError && !wallet.length
                                            ? <p className="text-muted mb-0">Nenhuma moeda com saldo neste momento.</p>
                                            : <></>}
                                        {wallet.length > 0 && (
                                            <div className="table-responsive">
                                                <table className="table table-hover align-middle mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>Moeda</th>
                                                            <th className="text-end">Quantidade</th>
                                                            <th className="text-end">Estimado USDC</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {wallet.map(row => (
                                                            <tr key={row.asset}>
                                                                <td className="fw-bold">{row.asset}</td>
                                                                <td className="text-end">{formatQty(row.available)}</td>
                                                                <td className="text-end">{formatUsd(row.fiatEstimate)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {allocated && (
                            <div className="card border-0 shadow mb-4">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h3 className="h5 mb-0">Carteira da plataforma (Binance)</h3>
                                        <Link to="/platform-wallet" className="small">Ver página completa</Link>
                                    </div>
                                    {data.platformWalletError
                                        ? <p className="text-muted mb-0">{data.platformWalletError}</p>
                                        : <></>}
                                    {!data.platformWalletError && !platformWallet.length
                                        ? <p className="text-muted mb-0">Nenhuma moeda com saldo na Binance neste momento.</p>
                                        : <></>}
                                    {platformWallet.length > 0 && (
                                        <>
                                            <p className="text-muted small mb-3">
                                                Total {formatUsd(data.platformWalletTotal)} USDC
                                                {Number(data.custodyReserved) > 0
                                                    ? ` · ${formatUsd(data.custodyReserved)} USDC reservado para clientes de custódia`
                                                    : ''}
                                            </p>
                                            <div className="table-responsive">
                                                <table className="table table-hover align-middle mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>Moeda</th>
                                                            <th className="text-end">Quantidade</th>
                                                            <th className="text-end">Estimado USDC</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {platformWallet.map(row => (
                                                            <tr key={row.asset}>
                                                                <td className="fw-bold">{row.asset}</td>
                                                                <td className="text-end">{formatQty(row.available)}</td>
                                                                <td className="text-end">{formatUsd(row.fiatEstimate)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="card border-0 shadow mb-4">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                    <h3 className="h5 mb-0">Histórico de PnL</h3>
                                    <div className="text-muted small">
                                        Hoje: <span className={toneClass(vault.today)}>{formatUsd(vault.today, true)} USDC</span>
                                        {' · '}
                                        {vault.count || 0} lucro{(vault.count || 0) === 1 ? '' : 's'}
                                        {' · '}
                                        {vault.lossCount || 0} stop{(vault.lossCount || 0) === 1 ? '' : 's'}
                                    </div>
                                </div>
                                {!entries.length
                                    ? <p className="text-muted mb-0">Ainda não há ciclo fechado para este usuário.</p>
                                    : (
                                        <div className="table-responsive">
                                            <table className="table table-hover align-middle mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Quando</th>
                                                        <th>Par</th>
                                                        <th>Tipo</th>
                                                        <th className="text-end">Resultado</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {entries.map(entry => {
                                                        const deposit = entry.isDeposit || String(entry.symbol || '').toUpperCase() === 'DEPOSIT';
                                                        const loss = !deposit && (Number(entry.profit) < 0 || entry.isLoss);
                                                        return (
                                                            <tr key={entry.id}>
                                                                <td>{formatWhen(entry.createdAt)}</td>
                                                                <td>{deposit ? 'Depósito' : pairLabel(entry.symbol)}</td>
                                                                <td>{deposit ? 'Depósito' : (loss ? 'Stop' : 'Lucro')}</td>
                                                                <td className={`text-end ${deposit ? 'text-info' : toneClass(entry.profit)}`}>
                                                                    {formatUsd(entry.profit, true)} USDC
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                            </div>
                        </div>
                    </>
                )}
                <Footer />
            </main>
            <UserModal data={modalUser} onSubmit={() => window.location.reload()} />
            {user.isCustody
                ? (
                    <>
                        <PnlModal data={data} onSubmit={() => loadOverview()} />
                        <DepositedModal data={data} onSubmit={() => loadOverview()} />
                        <WalletModal data={data} onSubmit={() => loadOverview()} />
                        <DepositModal data={data} onSubmit={() => loadOverview()} />
                    </>
                )
                : <></>}
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

export default UserDetail;
